// src/pages/ViewOrderPage.js
// FIXED CODE

import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { get } from 'aws-amplify/api';
import {
    Heading,
    Flex,
    Text,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Button,
    View, // Import View for layout
} from '@aws-amplify/ui-react';

/** Convert decimal hours (e.g. 1.37) to "H:MM" */
function decimalHoursToHM(decimalValue) {
    if (decimalValue === null || decimalValue === undefined) return '';
    const hours = Math.floor(decimalValue);
    const fractional = decimalValue - hours;
    const minutes = Math.round(fractional * 60);
    return `${hours}:${String(minutes).padStart(2, '0')}`;
}

export default function ViewOrderPage() {
    const { id } = useParams();
    const orderId = decodeURIComponent(id);

    const [order, setOrder] = useState(null);
    const [errorMsg, setErrorMsg] = useState('');
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const fetchOrder = async () => {
            try {
                setLoading(true);
                setErrorMsg('');

                const restOp = get({
                    apiName: 'cbx_CRUD_API',
                    path: `/unifiedorders/${encodeURIComponent(orderId)}`,
                });
                const response = await restOp.response;
                if (response.statusCode < 200 || response.statusCode > 299) {
                    if (response.statusCode === 404) {
                        throw new Error(`Order with ID '${orderId}' not found.`);
                    }
                    throw new Error(
                        `HTTP ${response.statusCode} error fetching order details.`
                    );
                }
                const data = await response.body.json();
                setOrder(data);
            } catch (error) {
                console.error('[ViewOrderPage] error:', error);
                setErrorMsg(error.message);
            } finally {
                setLoading(false);
            }
        };
        fetchOrder();
    }, [orderId]);

    // Simple helper for potentially nested values (like DynamoDB format)
    function getValue(attr) {
        if (typeof attr === 'object' && attr !== null) {
            if ('S' in attr) return attr.S;
            if ('N' in attr) return attr.N; // Keep as number potentially
            if ('BOOL' in attr) return attr.BOOL;
            if ('NULL' in attr && attr.NULL === true) return null;
            // Could add 'M' or 'L' handling if needed, but unlikely here
        }
        return attr; // Assume plain value otherwise
    }

    const dateStr = order?.orderDate
        ? new Date(order.orderDate).toLocaleDateString('en-NZ')
        : 'N/A';
    const billing = order?.billingAddress || {}; // Already likely flattened by backend/list page logic

    if (loading) {
        return <View padding="1rem">Loading order details...</View>;
    }
    if (errorMsg) {
        return <View padding="1rem" style={{ color: 'red' }}>Error: {errorMsg}</View>;
    }
    if (!order) {
        // This case might be covered by 404 error, but good fallback
        return <View padding="1rem">Order not found.</View>;
    }

    // Use getValue safely for top-level fields too
    const displayOrderId = getValue(order.orderId) || 'N/A';
    const orderStatus = getValue(order.OrderStatus) || 'N/A';
    const customerId = getValue(order.customerId) || 'N/A';
    const totalAmount = getValue(order.totalAmount); // Could be number
    const source = getValue(order.source) || 'N/A';

    return (
        <View padding="1rem">
            <Flex justifyContent="space-between" alignItems="center" marginBottom="1rem">
                <Heading level={3}>Order Details: {displayOrderId}</Heading>
                <Link to={`/update/${encodeURIComponent(displayOrderId)}`}>
                    <Button variation="primary">Update Order</Button>
                </Link>
            </Flex>

            <Flex direction="column" gap="0.5rem" marginBottom="1rem">
                <Text><strong>Order Date:</strong> {dateStr}</Text>
                <Text><strong>Order Status:</strong> {orderStatus}</Text>
                <Text><strong>Customer ID:</strong> {customerId}</Text>
                <Text><strong>Total Amount:</strong> ${typeof totalAmount === 'number' ? totalAmount.toFixed(2) : (totalAmount || '0.00')}</Text>
                <Text><strong>Source:</strong> {source}</Text>
            </Flex>

            <Heading level={5}>Billing Address</Heading>
            {Object.keys(billing).length > 0 ? (
                <Flex direction="column" gap="0.3rem" marginBottom="1rem">
                    {/* Use getValue for billing fields, assuming they might still be nested */}
                    <Text>Contact: {getValue(billing.Contact) || 'N/A'}</Text>
                    <Text>Address1: {getValue(billing.Address1) || 'N/A'}</Text>
                    <Text>Address2: {getValue(billing.Address2)}</Text> {/* Optional fields */}
                    <Text>Address3: {getValue(billing.Address3)}</Text>
                    <Text>Suburb: {getValue(billing.Suburb) || 'N/A'}</Text>
                    <Text>State: {getValue(billing.State) || 'N/A'}</Text>
                    <Text>Country: {getValue(billing.Country) || 'N/A'}</Text>
                </Flex>
            ) : (
                <Text>No billing address provided.</Text>
            )}

            <Heading level={5}>Line Items</Heading>
            {Array.isArray(order.items) && order.items.length > 0 ? (
                <View style={{ overflowX: 'auto', marginBottom: '1rem' }}> {/* Wrapper for horizontal scroll */}
                    <Table variation="striped" size="small" style={{ width: '100%', minWidth: '1200px' }}>
                        <TableHead>
                            <TableRow>
                                <TableCell as="th">Product ID</TableCell>
                                <TableCell as="th">Price</TableCell>
                                <TableCell as="th">Quantity</TableCell>
                                <TableCell as="th">Board Grade</TableCell>
                                <TableCell as="th">Box Style</TableCell>
                                <TableCell as="th">Incl. Lid</TableCell>
                                <TableCell as="th">Length</TableCell>
                                <TableCell as="th">Width</TableCell>
                                <TableCell as="th">Height</TableCell>
                                <TableCell as="th">Tot. Time</TableCell>
                                <TableCell as="th">Tot. Sheets</TableCell>
                                <TableCell as="th">Tot. Length</TableCell>
                                <TableCell as="th">Tot. Width</TableCell>
                                <TableCell as="th">GT Narration</TableCell> {/* Changed Label */}
                                <TableCell as="th">Notes</TableCell> {/* Added New Notes */}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {order.items.map((oneItem, idx) => {
                                // Properties are nested under item, might still be Dynamo format
                                const p = oneItem.properties || {};
                                let timeDisplay = '';
                                const totalTimeRaw = getValue(p._totalTime); // Get potentially numeric value
                                if (totalTimeRaw !== null && totalTimeRaw !== undefined) {
                                    timeDisplay = decimalHoursToHM(parseFloat(totalTimeRaw));
                                }

                                return (
                                    <TableRow key={idx}>
                                        <TableCell>{getValue(oneItem.productId) || 'N/A'}</TableCell>
                                        <TableCell>{getValue(oneItem.price) ?? '0'}</TableCell>
                                        <TableCell>{getValue(oneItem.quantity) ?? '0'}</TableCell>
                                        <TableCell>{getValue(p.Board_Grade) || 'N/A'}</TableCell>
                                        <TableCell>{getValue(p.Box_Style) || 'N/A'}</TableCell>
                                        <TableCell>{getValue(p.Include_Lid) || 'No'}</TableCell>
                                        <TableCell>{getValue(p.Length) || getValue(p.length) || ''}</TableCell> {/* Check both cases */}
                                        <TableCell>{getValue(p.Width) || getValue(p.width) || ''}</TableCell>
                                        <TableCell>{getValue(p.Height) || getValue(p.height) || ''}</TableCell>
                                        <TableCell>{timeDisplay}</TableCell>
                                        <TableCell>{getValue(p._totalSheets) || ''}</TableCell>
                                        <TableCell>{getValue(p._totalLength) || ''}</TableCell>
                                        <TableCell>{getValue(p._totalWidth) || ''}</TableCell>
                                        <TableCell>{getValue(p.notes) || ''}</TableCell> {/* GT Narration */}
                                        <TableCell>{getValue(p.adminNotes) || ''}</TableCell> {/* New Notes */}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </View>
            ) : (
                <Text>No line items found for this order.</Text>
            )}

            {/* Moved update button to the top */}
        </View>
    );
}